import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";
import Grain from "@/components/Grain";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SESSION_KEY = "cc_admin_key";

function PasscodeGate({ onUnlock }) {
  const [pass, setPass] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!pass.trim()) {
      toast.error("Enter passcode.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/rsvps`, {
        headers: { "X-Admin-Key": pass },
      });
      sessionStorage.setItem(SESSION_KEY, pass);
      onUnlock(pass, res.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) toast.error("Wrong passcode.");
      else toast.error("Could not authenticate. Try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <form
        onSubmit={submit}
        className="bg-white"
        style={{ border: "1px solid var(--vein)", padding: 40, width: "min(440px, 92vw)" }}
      >
        <div className="overline">Cara Capital · Admin</div>
        <h1 className="font-serif mt-4" style={{ fontSize: 36, lineHeight: 1.05, letterSpacing: "-0.02em" }}>
          Guest list <span className="italic-serif grad-text">access.</span>
        </h1>
        <div className="field mt-10">
          <label htmlFor="passcode">Passcode</label>
          <input
            id="passcode"
            data-testid={ADMIN.passcodeInput}
            type="password"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
            autoFocus
            autoComplete="off"
          />
        </div>
        <button
          type="submit"
          className="btn btn-ember mt-6"
          data-testid={ADMIN.passcodeSubmit}
          disabled={loading}
          style={{ width: "100%" }}
        >
          {loading ? "Verifying\u2026" : "Unlock"}
        </button>
      </form>
    </div>
  );
}

function escapeCsv(v) {
  const s = v === undefined || v === null ? "" : String(v);
  return `"${s.replace(/"/g, '""')}"`;
}

function downloadCsv(rows) {
  const headers = ["first_name", "last_name", "email", "phone", "company_name", "created_at", "id"];
  const lines = [headers.map((h) => escapeCsv(h)).join(",")];
  rows.forEach((r) => {
    lines.push(headers.map((h) => escapeCsv(r[h])).join(","));
  });
  const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "cara-capital-guest-list.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function Dashboard({ passkey, initial, onLogout }) {
  const [rows, setRows] = useState(initial || []);
  const [loading, setLoading] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/rsvps`, { headers: { "X-Admin-Key": passkey } });
      setRows(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired.");
        onLogout();
      } else {
        toast.error("Could not refresh.");
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!initial) refresh();
     
  }, []);

  const empty = rows.length === 0;

  return (
    <div className="min-h-screen">
      <div className="container-editorial pt-10 md:pt-16 pb-6 flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="overline">Cara Capital · Admin</div>
          <h1 className="font-serif mt-4" style={{ fontSize: "clamp(36px, 4vw, 56px)", lineHeight: 1.02, letterSpacing: "-0.02em" }}>
            Guest list <span className="italic-serif grad-text">& invitations.</span>
          </h1>
          <div data-testid={ADMIN.count} className="mt-4 text-[14px]" style={{ color: "var(--stone)" }}>
            {rows.length} {rows.length === 1 ? "request" : "requests"}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid={ADMIN.exportCsv}
            onClick={() => downloadCsv(rows)}
            className="btn btn-outline"
            disabled={empty}
            style={{ opacity: empty ? 0.5 : 1, cursor: empty ? "not-allowed" : "pointer" }}
            aria-disabled={empty}
          >
            Export CSV
          </button>
          <button
            onClick={refresh}
            className="btn btn-outline"
            style={{ opacity: loading ? 0.6 : 1 }}
            disabled={loading}
          >
            {loading ? "Refreshing\u2026" : "Refresh"}
          </button>
          <button
            data-testid={ADMIN.logoutBtn}
            onClick={onLogout}
            className="btn btn-ember"
          >
            Log out
          </button>
        </div>
      </div>

      <div className="container-editorial pb-24">
        <div className="bg-white" style={{ border: "1px solid var(--vein)", overflowX: "auto" }}>
          <table data-testid={ADMIN.table} className="rsvp-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Company</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {empty && (
                <tr>
                  <td colSpan={5} style={{ padding: "32px 16px", color: "var(--stone)", textAlign: "center" }}>
                    No RSVPs yet. Submissions from the landing page will appear here.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id}>
                  <td style={{ color: "var(--ink)" }}>{r.first_name} {r.last_name}</td>
                  <td><a href={`mailto:${r.email}`} style={{ color: "var(--ember)", textDecoration: "none" }}>{r.email}</a></td>
                  <td>{r.phone}</td>
                  <td>{r.company_name}</td>
                  <td style={{ color: "var(--stone)" }}>{new Date(r.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [unlocked, setUnlocked] = useState(false);
  const [passkey, setPasskey] = useState("");
  const [initial, setInitial] = useState(null);

  useEffect(() => {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (saved) {
      axios
        .get(`${API}/rsvps`, { headers: { "X-Admin-Key": saved } })
        .then((res) => {
          setPasskey(saved);
          setInitial(res.data);
          setUnlocked(true);
        })
        .catch(() => {
          sessionStorage.removeItem(SESSION_KEY);
        });
    }
  }, []);

  function logout() {
    sessionStorage.removeItem(SESSION_KEY);
    setPasskey("");
    setUnlocked(false);
    setInitial(null);
  }

  return (
    <div className="bg-marble" style={{ position: "relative" }}>
      <Grain />
      {!unlocked ? (
        <PasscodeGate
          onUnlock={(p, data) => {
            setPasskey(p);
            setInitial(data);
            setUnlocked(true);
            toast.success("Welcome.");
          }}
        />
      ) : (
        <Dashboard passkey={passkey} initial={initial} onLogout={logout} />
      )}
    </div>
  );
}
